/**
 * The leave funnel's state: the fan's own figures, which panel is doing what, and the write.
 *
 * The same split ./useJoinFunnel.js makes, in the other direction -- everything stateful lives here
 * so that blocks/ProfileBlock.jsx is only about which dialog is on screen and what it is handed.
 * The sequence itself is ./leaveLoyalty.js, which is pure and tested; this is the wiring that points
 * it at the real browser.
 *
 * **The two dialogs read NO client**, by the library's design: every string and every figure on them
 * is the host's, because a library default for "your points will be removed" would be a legal claim
 * the library cannot make. So the figures are fetched here.
 */

import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { formatNumber } from '@drewling/urc-loyalty-api';
import { useLoyaltyData } from '@drewling/urc-loyalty-ui';
import { withdrawLoyaltyConsent } from './consent';
import { describeLoyaltyError } from './errorDetail';
import { formatMemberSince, formatPoints, interpolate } from './membershipCopy';
import { LEAVE_OUTCOME, createLeaveLoyalty, rememberLeaveOutcome } from './leaveLoyalty';
import { forgetEnrolled } from './toasts/enrolled';
import { authState } from './urcAuth';
import { createBrowserWithdrawal } from './withdrawal';
import { membership as membershipClient } from './membership';

/**
 * Hoisted for a stable identity, and each one calls the SAME method a card on this page already
 * calls -- `TierProgressCard` reads the progress and `BadgesBlock` the badges overview. The badges
 * read is memoised by method name in ./client.js's prefetch cache, so it is the same promise rather
 * than a second request; the progress read is collapsed by the coalescer while both are in flight.
 * Neither figure is worth a request of its own, and the dialog must not disagree with the card
 * behind it.
 *
 * @param {Object} api  The injected loyalty client.
 * @param {Object} opts `{signal}`, for abort-on-unmount.
 * @return {Promise<Object>} The slice.
 */
const fetchTierProgress = ( api, opts ) => api.getTierProgress( opts );

/**
 * @param {Object} api  The injected loyalty client.
 * @param {Object} opts `{signal}`, for abort-on-unmount.
 * @return {Promise<Object>} The slice.
 */
const fetchBadgesDetail = ( api, opts ) => api.getBadgesDetail( opts );

/**
 * The profile, read ONLY for `accountCreatedAt` -- the membership card's "Member since ..." line.
 *
 * **It is not a third request.** `getTierProgress` above already fetches `/api/users/me` below its
 * own surface, this slice mounts in the same commit, and ./client.js wraps the client in
 * `withCoalescedFetch`, which collapses concurrent identical GETs. The profile hero on this page
 * calls `getProfile` too and joins the same collapse -- a cold signed-in load issues one
 * `/api/users/me`, measured when 0.15.0 landed.
 *
 * Deliberately NOT added to `PREFETCH_METHODS` in ./client.js, and the reason is a side effect
 * rather than tidiness: that memo is keyed by method name across the whole page, and a memoised
 * `getProfile` silently disables the profile hero's own `revalidation.refresh()`. Nothing hovers
 * this dialog's trigger anyway, and the coalescer has already paid the cost the memo would.
 *
 * @param {Object} api  The injected loyalty client.
 * @param {Object} opts `{signal}`, for abort-on-unmount.
 * @return {Promise<Object>} The slice.
 */
const fetchProfile = ( api, opts ) => api.getProfile( opts );

/**
 * **Both label groups arrive named, and the naming IS the fix.**
 *
 * This hook composes two surfaces at once: the leave dialog's sentences, which are the `leave`
 * group's, and the membership card, which belongs to the MANAGE dialog and is authored on that tab.
 * It used to take one parameter called `labels`, and a parameter with that name can be dotted into
 * without anybody saying WHICH dialog -- so the card's points unit was read off the `leave` group,
 * where App\Loyalty\MembershipLabels has never filed it, and came back `undefined`. `formatPoints`
 * answers a bare figure for a missing unit by design, so a fan read "AMATEUR · 425" while the ACF
 * field said PTS all along, with nothing anywhere reporting a fault.
 *
 * After this rename no identifier of that name exists in here, so the reach is not expressible.
 * ./membershipLabels.test.js is the other half: it checks every key read off either group against
 * the PHP map, which is the seam no type covers.
 *
 * @param {Object}  options              Wiring.
 * @param {Object}  options.leaveLabels  The `leave` group: the leave dialog's own copy.
 * @param {Object}  options.manageLabels The `manage` group. The membership card's copy is a
 *                                       manage-dialog concern even though this hook composes it.
 * @param {Object}  options.settings     `window.urcLoyalty`'s consent block: eraseData,
 *                                       writeDeadlineMs.
 * @param {boolean} options.offered      Whether there is anywhere to send a fan who leaves. When
 *                                       there is not, no dialog can ever open, so neither read is
 *                                       made.
 * @return {Object} The funnel's state and the two actions that move it.
 */
export function useLeaveLoyalty( { leaveLabels, manageLabels, settings, offered } ) {
	/*
	 * `null` rather than `undefined` is what suppresses the read: `useLoyaltyData` treats ANY
	 * defined `override` as "this slice never touches the client". So a profile page whose block
	 * names no destination costs nothing at all for a feature it cannot offer -- which matters
	 * because this hook runs on every one of them, and `getTierProgress` is not in the prefetch
	 * memo the way `getBadgesDetail` is.
	 *
	 * The `data !== null` guards below are the other half of it: an overridden slice reports
	 * `success` carrying exactly what it was given.
	 */
	const skip = offered ? undefined : null;
	const tier = useLoyaltyData( fetchTierProgress, skip );
	const badges = useLoyaltyData( fetchBadgesDetail, skip );
	const profile = useLoyaltyData( fetchProfile, skip );

	// 'idle' | 'leaving' | 'failed'. Not a fourth state for success: the flow ends in a navigation,
	// so there is nothing to render afterwards and a 'left' state would only ever be seen for the
	// frame before the page went away.
	const [ phase, setPhase ] = useState( 'idle' );

	// A ref and not the phase, because both dialogs' buttons have to be inert the instant the first
	// click lands -- a second click inside the same commit would otherwise start a second chain.
	// `ModalActions` hardcodes `disabled: false` on the control it draws, so there is no disabled
	// flag available and this is the whole of the guard.
	const running = useRef( false );

	const leave = useMemo( () => {
		return createLeaveLoyalty( {
			// The ID token, through the consent module, because this endpoint is ours. The
			// withdrawal client carries the ACCESS token, because those endpoints are 3Halves'.
			withdrawConsent: withdrawLoyaltyConsent,
			withdrawal: createBrowserWithdrawal(),
			// The journal transaction, when this page has its routes; the two lines above are then
			// only the fallback for a page cached before them. See ./leaveLoyalty.js.
			membership: membershipClient(),
			eraseData: settings.eraseData === true,
			forget: forgetEnrolled,
			remember: rememberLeaveOutcome,
			deadlineMs: Number( settings.writeDeadlineMs ) || undefined,
			warn: ( message, detail ) =>
				console.warn(
					`[loyalty] ${ message }`,
					detail && detail.error ? describeLoyaltyError( detail.error ) : detail
				),
		} );
	}, [ settings.eraseData, settings.writeDeadlineMs ] );

	/**
	 * The membership card's two required values, or undefined until the read settles.
	 *
	 * Undefined is what keeps the manage link off the page: `ManageLoyaltyModal.membership` is
	 * required, and a card drawn with a placeholder tier and no points is worse than a link that
	 * appears a moment later.
	 *
	 * **The join date is the third value and it gates NOTHING, which is the load-bearing asymmetry
	 * here.** The early return is keyed on the TIER alone. If the profile read joined it, a slow or
	 * failed `getProfile` would take the manage link off the page -- and that link is the fan's only
	 * way out of the programme, so a decorative line failing to load would withdraw a consent
	 * control. That is the asymmetry D-088 says this surface may never have. A failed date read
	 * costs the line and nothing else.
	 *
	 * The key is ASSIGNED CONDITIONALLY rather than spread in as `undefined`: `MembershipCard`
	 * branches on `=== undefined` so either spelling draws correctly today, but the absent key is
	 * what the library's own contract is written against, and ../membershipCopy.js is where the
	 * "never a blank string" rule is enforced and tested.
	 */
	const membership = useMemo( () => {
		if ( tier.status !== 'success' || tier.data === null ) {
			return undefined;
		}

		const card = {
			// Also what selects the crest on the card, through the library's own tier table.
			tierName: tier.data.currentTierName,
			// Formatted here, with the library's own formatter, so this figure and the one on the
			// tier card behind the dialog cannot be written differently.
			points: formatPoints(
				formatNumber( tier.data.currentPoints ),
				manageLabels.pointsSuffix
			),
		};

		// `accountCreatedAt` is when the fan's record was created AT THE PROVIDER. It is within
		// seconds of their joining for the fan this was measured on, and it is not contractually
		// the join moment -- which is why the sentence naming it is the editor's to write, and why
		// the field ships blank. See App\Loyalty\MembershipLabels::OPTIONAL.
		const memberSince =
			profile.status === 'success' && profile.data !== null
				? formatMemberSince( manageLabels.memberSince, profile.data.accountCreatedAt )
				: undefined;

		if ( memberSince !== undefined ) {
			card.memberSince = memberSince;
		}

		return card;
	}, [ tier, profile, manageLabels.pointsSuffix, manageLabels.memberSince ] );

	/**
	 * The leave dialog's paragraph, with the fan's own figures in it where they are all available.
	 *
	 * Total: one missing figure takes the whole sentence to the plain wording. A fan deciding
	 * whether to leave must not be shown a number we could not read, and must never be shown a
	 * literal `{points}`.
	 */
	const body = useMemo( () => {
		const values = {};

		if ( tier.status === 'success' && tier.data !== null ) {
			values.points = formatNumber( tier.data.currentPoints );
			values.tier = tier.data.currentTierName;
		}

		if ( badges.status === 'success' && badges.data !== null ) {
			values.badges = formatNumber( badges.data.summary.earned );
		}

		return interpolate( leaveLabels.body, leaveLabels.bodyPlain, values );
	}, [ tier, badges, leaveLabels.body, leaveLabels.bodyPlain ] );

	/**
	 * Start again, on an account switch.
	 *
	 * `resources/scripts/loyalty.jsx` repaints the same component on a change of fan rather than
	 * remounting it, so state written for one fan survives into the next one's page without this.
	 */
	const reset = useCallback( () => {
		running.current = false;
		setPhase( 'idle' );
	}, [] );

	/**
	 * Leave, and go wherever the block says.
	 *
	 * @param {string} destination Where to send the fan afterwards.
	 * @return {void}
	 */
	const submit = useCallback(
		destination => {
			if ( running.current ) {
				return;
			}

			running.current = true;
			setPhase( 'leaving' );

			const state = authState();
			const fan = state && state.sub ? state.sub : '';

			leave( fan ).then( result => {
				if ( result.outcome === LEAVE_OUTCOME.CONSENT_WRITE_FAILED ) {
					// Nothing was recorded, so the fan is still a member and has to be told. The
					// dialog stays open with its own error wording; the page is not left.
					running.current = false;
					setPhase( 'failed' );

					return;
				}

				// Every other outcome means the fan has left. `running` is deliberately NOT cleared:
				// the navigation is about to destroy this document, and a re-armed button in the
				// frames before it lands could only start a second chain.
				window.location.replace( destination );
			} );
		},
		[ leave ]
	);

	return { membership, body, phase, reset, submit };
}
